import React from "react";
import { SelectChangeEvent } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useTranslation } from "react-i18next";
import {
  ArcButton,
  ArcCheckbox,
  ArcIconButton,
  ArcMenuItem,
  ArcOutlinedSelect,
  ArcStack,
  ArcText,
} from "@src/core/ui";
import { OrderStatusEnum } from "@src/features/order/entities";
import { useGetHubsQuery } from "@src/features/hubs/redux";
import {
  DATE_PRESET_LABEL_KEYS,
  DATE_PRESET_OPTIONS,
  DEFAULT_ORDER_FILTERS,
  DROPDOWN_MENU_PROPS,
  ORDER_TYPE_OPTIONS,
} from "./constants";
import { countAppliedFilters, getDateFilterLabel, isCustomRangeIncomplete } from "./date-range";
import { ALL_HUBS, ALL_ORDERS, DatePreset, OrderFilters } from "./types";
import DateRangeCalendar from "./date-range-calendar";

interface FilterPanelProps {
  initialFilters: OrderFilters;
  canFilterByHub?: boolean;
  onApply: (filters: OrderFilters) => void;
  onClose: () => void;
}

type DateSelection = Pick<OrderFilters, "datePreset" | "customStart" | "customEnd">;

const resolveSelection = (next: string[], sentinel: string): string[] =>
  next[next.length - 1] === sentinel ? [] : next.filter((value) => value !== sentinel);

const checkboxSx = { p: 0, mr: 1 } as const;

const FilterPanel: React.FC<FilterPanelProps> = ({
  initialFilters,
  canFilterByHub = true,
  onApply,
  onClose,
}) => {
  const { t } = useTranslation();
  const { data: hubs = [] } = useGetHubsQuery(undefined, { skip: !canFilterByHub });
  const [draft, setDraft] = React.useState<OrderFilters>(initialFilters);
  const [dateFallback, setDateFallback] = React.useState<DateSelection | null>(null);

  const hasSelection = countAppliedFilters(draft) > 0;
  const applyDisabled = isCustomRangeIncomplete(draft);

  const patch = (changes: Partial<OrderFilters>) => setDraft((prev) => ({ ...prev, ...changes }));

  const onOrderTypesChange = (event: SelectChangeEvent<unknown>) =>
    patch({
      orderTypes: resolveSelection(
        event.target.value as string[],
        ALL_ORDERS,
      ) as OrderStatusEnum[],
    });

  const onHubIdsChange = (event: SelectChangeEvent<unknown>) =>
    patch({
      hubIds: resolveSelection(event.target.value as string[], ALL_HUBS).map(Number),
    });

  const onDatePresetChange = (event: SelectChangeEvent<unknown>) => {
    const datePreset = event.target.value as DatePreset;

    if (datePreset === DatePreset.CUSTOM) {
      const { customStart, customEnd } = draft;
      setDateFallback({ datePreset: draft.datePreset, customStart, customEnd });
      patch({ datePreset });
      return;
    }

    setDateFallback(null);
    patch({ datePreset, customStart: null, customEnd: null });
  };

  const cancelCalendar = () => {
    if (dateFallback) setDraft((prev) => ({ ...prev, ...dateFallback }));
    setDateFallback(null);
  };

  const renderOrderTypes = () =>
    draft.orderTypes.length
      ? draft.orderTypes.map((status) => t(`history.filter.orderType.${status}`)).join(", ")
      : t("history.filter.orderType.all");

  const renderHubs = () =>
    draft.hubIds.length
      ? draft.hubIds
        .map(
          (hubId) =>
            hubs.find((hub) => hub.id === hubId)?.name ??
              t("history.filter.hub.unknown", { id: hubId }),
        )
        .join(", ")
      : t("history.filter.hub.all");

  return (
    <ArcStack height="100%" direction="column" justifyContent="space-between" sx={{ minHeight: 0 }}>
      <ArcStack spacing={2} sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
        <ArcStack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ pr: 0.5 }}>
          <ArcText.h4 fontSize={18} fontWeight={500} color="common.white">
            {t("history.filter.title")}
          </ArcText.h4>
          <ArcIconButton
            size="small"
            onClick={onClose}
            aria-label={t("history.filter.close")}
            sx={{ p: 0 }}>
            <CloseIcon sx={{ color: "common.white" }} />
          </ArcIconButton>
        </ArcStack>

        <ArcStack spacing={2} pt={1.5}>
          <ArcOutlinedSelect
            label={t("history.filter.orderType.label")}
            fullWidth
            multiple
            value={draft.orderTypes.length ? draft.orderTypes : [ALL_ORDERS]}
            onChange={onOrderTypesChange}
            renderValue={renderOrderTypes}
            MenuProps={DROPDOWN_MENU_PROPS}>
            <ArcMenuItem value={ALL_ORDERS}>
              <ArcCheckbox
                size="small"
                color="brand"
                sx={checkboxSx}
                checked={draft.orderTypes.length === 0}
              />
              {t("history.filter.orderType.all")}
            </ArcMenuItem>
            {ORDER_TYPE_OPTIONS.map((option) => (
              <ArcMenuItem key={option.value} value={option.value}>
                <ArcCheckbox
                  size="small"
                  color="brand"
                  sx={checkboxSx}
                  checked={draft.orderTypes.includes(option.value)}
                />
                {t(option.labelKey)}
              </ArcMenuItem>
            ))}
          </ArcOutlinedSelect>

          {canFilterByHub && (
            <ArcOutlinedSelect
              label={t("history.filter.hub.label")}
              fullWidth
              multiple
              value={draft.hubIds.length ? draft.hubIds.map(String) : [ALL_HUBS]}
              onChange={onHubIdsChange}
              renderValue={renderHubs}
              MenuProps={DROPDOWN_MENU_PROPS}>
              <ArcMenuItem value={ALL_HUBS}>
                <ArcCheckbox
                  size="small"
                  color="brand"
                  sx={checkboxSx}
                  checked={draft.hubIds.length === 0}
                />
                {t("history.filter.hub.all")}
              </ArcMenuItem>
              {hubs.map((hub) => (
                <ArcMenuItem key={hub.id} value={String(hub.id)}>
                  <ArcCheckbox
                    size="small"
                    color="brand"
                    sx={checkboxSx}
                    checked={draft.hubIds.includes(hub.id)}
                  />
                  {hub.name}
                </ArcMenuItem>
              ))}
            </ArcOutlinedSelect>
          )}

          <ArcStack sx={dateFallback ? { "& .MuiFormHelperText-root": { display: "none" } } : undefined}>
            <ArcOutlinedSelect
              label={t("history.filter.date.label")}
              fullWidth
              value={draft.datePreset}
              onChange={onDatePresetChange}
              renderValue={() => getDateFilterLabel(draft, t)}
              MenuProps={DROPDOWN_MENU_PROPS}>
              {DATE_PRESET_OPTIONS.map((preset) => (
                <ArcMenuItem key={preset} value={preset}>
                  {t(DATE_PRESET_LABEL_KEYS[preset])}
                </ArcMenuItem>
              ))}
            </ArcOutlinedSelect>

            {dateFallback && (
              <DateRangeCalendar
                start={draft.customStart}
                end={draft.customEnd}
                onChange={(customStart, customEnd) => patch({ customStart, customEnd })}
                onCancel={cancelCalendar}
                onApply={() => setDateFallback(null)}
              />
            )}
          </ArcStack>
        </ArcStack>
      </ArcStack>

      <ArcStack flexShrink={0} spacing={1} pt={2}>
        {hasSelection ? (
          <>
            <ArcStack direction="row" spacing={1} width="100%">
              <ArcButton.ghost fullWidth size="large" type="button" onClick={onClose}>
                {t("history.filter.actions.cancel")}
              </ArcButton.ghost>
              <ArcButton.ghost
                fullWidth
                size="large"
                type="button"
                onClick={() => setDraft(DEFAULT_ORDER_FILTERS)}>
                {t("history.filter.actions.reset")}
              </ArcButton.ghost>
            </ArcStack>
            <ArcButton.brand
              fullWidth
              size="large"
              type="button"
              disabled={applyDisabled}
              onClick={() => onApply(draft)}>
              {t("history.filter.actions.apply")}
            </ArcButton.brand>
          </>
        ) : (
          <ArcStack direction="row" spacing={1} width="100%">
            <ArcButton.ghost fullWidth size="large" type="button" onClick={onClose}>
              {t("history.filter.actions.cancel")}
            </ArcButton.ghost>
            <ArcButton.brand
              fullWidth
              size="large"
              type="button"
              disabled={applyDisabled}
              onClick={() => onApply(draft)}>
              {t("history.filter.actions.apply")}
            </ArcButton.brand>
          </ArcStack>
        )}
      </ArcStack>
    </ArcStack>
  );
};

export default FilterPanel;
